import {
    MAX_CACHE_ENTRIES,
    PERSISTED_RENDER_MAX_SIDE,
    PERSISTED_RENDER_WEBP_QUALITY,
} from './skinRenderShared';

const DEFAULT_SKIN_ASSETS_URL = '/skin-assets/';
const RENDER_SCALE = 4;
const RENDER_DIRECTION = 1;
// The idle poses we ship are 48-frame loops at the bone's own frame rate; the
// snapshot path renders frame 0 and the animator plays the whole thing. Sound
// is off because the asset export ships no sound banks — leaving it on makes
// the audio manager chase files that were never extracted.
const PLAYBACK_AUDIO = false;

// Pose selection. Animation names embed the breed number
// (`AnimStatiqueExploNewAge9_1`, `AnimStatiqueCombat9a_1`), so they cannot be
// matched against a fixed string — rank by substring instead, best first.
//
// Combat ranks first, and on a class bone that is the pose worth having: the
// class's own stance, with its effects. On the generic bone it degrades to an
// anonymous fists-up guard, which is why this only reads well alongside
// `getClassBoneName`.
const STATIC_ANIM_RANK = [
    'StatiqueCombat',
    'StatiqueExploNewAge',
    'StatiqueExploRetro',
    'StatiqueExplo',
    'Statique',
];
// `_to_` names are transitions between two poses (AnimCueillir0_to_AnimStatiqueExplo0_1)
// — they match the ranking by substring but are not poses, and the asset export
// prunes them, so asking for one requests a file that was never shipped.
const STATIC_ANIM_EXCLUDE = ['_to_'];
// The pose the character is *measured* against, which is not the pose it is
// drawn in. Deliberately the plain exploration idle on the generic skeleton:
// upright, empty-handed, no effects, so its height tracks the body. A class
// stance does not — a bent Enutrof measures short and a Huppermage's raised
// blade measures tall — and measuring against one scales the character by its
// pose instead of holding it still.
const SIZE_REFERENCE_ANIM_RANK = ['StatiqueExplo', 'Statique'];
// Classes whose combat stance drops the thing that identifies them, and whose
// standing idle keeps it: the Forgelance's lance, the Zobal's mask. Nothing in
// the data says which pose holds a prop, so this is by eye, and keyed by the
// breed because the bone name is what the renderer is handed.
const CLASS_IDLE_FIRST = ['StatiqueExploNewAge', ...STATIC_ANIM_RANK];

/** The stances a character can be shown in, in the order the control cycles —
 *  so the first entry is what that class arrives in.
 *
 *  All of them are already on disk for every class, since the export keeps every
 *  Statique pose, so this costs nothing to offer. `ExploRetro` is a third one we
 *  ship (5.57 MB) and have never shown; adding it is one entry per order below.
 *
 *  A class whose combat stance is the wrong picture of it — a Forgelance's is
 *  empty-handed, a Zobal's unmasked — leads with the idle instead. That is a
 *  different default, not a restriction: both stances stay reachable, which is
 *  why this reorders rather than substituting.
 *
 *  `neutral` is the plain idle every class was drawn in before the class
 *  skeletons: no stance, no props, no effects. It only exists on the generic
 *  skeleton, so it is the one stance that changes which bone is loaded.
 */
const DEFAULT_STANCE_ORDER = ['combat', 'idle', 'neutral'];
const IDLE_FIRST_ORDER = ['idle', 'combat', 'neutral'];
const STANCE_ORDER_BY_BREED = {
    14: IDLE_FIRST_ORDER,  // zobal — the mask is in the idle
    20: IDLE_FIRST_ORDER,  // forgelance — the lance is in the idle
};

/** Classes that stand too low on the platform, by stance: a CSS translate on the
 *  character frame, negative is up. By eye — the Enutrof's bent stances put its
 *  feet below where the root says they are, the combat crouch most of all. */
const FOOT_LIFT_BY_BREED = {
    3: { combat: '-5%', idle: '-3%', neutral: '-3%' },  // enutrof
};

function breedOfBone(boneName) {
    return Number(String(boneName || '').split('-')[1]);
}

/** The stances this skeleton offers, best first, by the breed its name carries. */
export function stanceOrderForBone(boneName) {
    return STANCE_ORDER_BY_BREED[breedOfBone(boneName)] || DEFAULT_STANCE_ORDER;
}

/** Vertical correction for this class in this stance, or null for none. */
export function footLiftForBone(boneName, stance) {
    return FOOT_LIFT_BY_BREED[breedOfBone(boneName)]?.[stance] ?? null;
}

/** The pose ranking for a stance. The neutral idle is deliberately the same
 *  pose everything is measured against, so it is also the one stance that needs
 *  no scale or footing correction. */
function poseRankFor(stance) {
    if (stance === 'neutral') return SIZE_REFERENCE_ANIM_RANK;
    return stance === 'idle' ? CLASS_IDLE_FIRST : STATIC_ANIM_RANK;
}

/** The skeleton a stance is drawn on. The class bones carry only their own
 *  poses, so the neutral idle drops back to the generic one. */
function boneForStance(boneName, stance) {
    return stance === 'neutral' ? null : boneName;
}

const imageCache = new Map();
const pendingRequests = new Map();

let rendererModule = null;
let rendererConfigured = false;

function getSkinAssetsUrl() {
    if (typeof window !== 'undefined' && window.__RUNTIME_CONFIG__?.REACT_APP_SKIN_ASSETS_URL) {
        return window.__RUNTIME_CONFIG__.REACT_APP_SKIN_ASSETS_URL;
    }
    if (process.env.REACT_APP_SKIN_ASSETS_URL) {
        return process.env.REACT_APP_SKIN_ASSETS_URL;
    }
    if (typeof window !== 'undefined' && window.location?.origin) {
        return `${window.location.origin}${DEFAULT_SKIN_ASSETS_URL}`;
    }
    return DEFAULT_SKIN_ASSETS_URL;
}

function normalizeAssetsBase(url) {
    return url.endsWith('/') ? url : `${url}/`;
}

async function loadRenderer() {
    if (!rendererModule) {
        rendererModule = await import('d3-ts-renderer');
    }
    if (!rendererConfigured) {
        const { configure } = rendererModule;
        configure({
            strategy: 'url',
            basePath: normalizeAssetsBase(getSkinAssetsUrl()),
            ImageExtension: 'webp',
        });
        rendererConfigured = true;
    }
    return rendererModule;
}

function rememberImageSnapshot(key, snapshot) {
    if (imageCache.has(key)) imageCache.delete(key);
    imageCache.set(key, snapshot);
    while (imageCache.size > MAX_CACHE_ENTRIES) {
        const [oldestKey, oldestSnapshot] = imageCache.entries().next().value;
        imageCache.delete(oldestKey);
        if (oldestSnapshot?.url) {
            URL.revokeObjectURL(oldestSnapshot.url);
        }
    }
}

function blobToDataUrl(blob) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(typeof reader.result === 'string' ? reader.result : null);
        reader.onerror = () => reject(reader.error || new Error('failed to encode rendered image'));
        reader.readAsDataURL(blob);
    });
}

async function blobToPersistedDataUrl(blob) {
    if (typeof createImageBitmap !== 'function' || typeof document === 'undefined') {
        return blobToDataUrl(blob);
    }
    try {
        const bitmap = await createImageBitmap(blob);
        const longest = Math.max(bitmap.width, bitmap.height) || 1;
        const scale = Math.min(1, PERSISTED_RENDER_MAX_SIDE / longest);
        const w = Math.max(1, Math.round(bitmap.width * scale));
        const h = Math.max(1, Math.round(bitmap.height * scale));
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(bitmap, 0, 0, w, h);
        bitmap.close?.();
        const dataUrl = canvas.toDataURL('image/webp', PERSISTED_RENDER_WEBP_QUALITY);
        return dataUrl?.startsWith('data:image/') ? dataUrl : blobToDataUrl(blob);
    } catch {
        return blobToDataUrl(blob);
    }
}

/** Pick the pose to render. Sorted so the result is deterministic rather than
 *  dependent on the animation map's insertion order. */
function pickStaticAnimation(sprite, ranking = STATIC_ANIM_RANK) {
    const dirSuffix = `_${RENDER_DIRECTION}`;
    const names = [...sprite.animations.keys()].sort();
    const facing = names.filter((name) => name.endsWith(dirSuffix));

    const allowed = (name) => !STATIC_ANIM_EXCLUDE.some((token) => name.includes(token));
    for (const candidates of [facing, names]) {
        for (const rank of ranking) {
            const hit = candidates.find((name) => name.includes(rank) && allowed(name));
            if (hit) return hit;
        }
    }
    return names.find((name) => name.includes('Statique') && allowed(name)) || null;
}

/** Build a sprite on `boneName`, falling back to the generic skeleton.
 *
 *  A bone the loader cannot fetch is quietly substituted with bone 666 — the
 *  emblem skeleton, two nodes and one frame — so a missing class bone renders an
 *  empty character instead of raising. Comparing the bone it actually loaded
 *  against the one asked for is what turns that into a fallback.
 */
async function createSprite(lookString, canvas, boneName, options = {}) {
    const { Look, DofusSprite } = await loadRenderer();
    const sprite = await DofusSprite.create(Look.fromString(lookString), canvas, boneName ? { ...options, boneName } : options);
    if (boneName && sprite.data?.m_Name !== boneName) {
        return DofusSprite.create(Look.fromString(lookString), canvas, options);
    }
    return sprite;
}

async function renderLookStringToBlob(lookString, boneName, stance) {
    const { saveToPng } = await loadRenderer();
    const canvas = document.createElement('canvas');
    const sprite = await createSprite(lookString, canvas, boneForStance(boneName, stance), { numberFrame: 1 });
    const animName = pickStaticAnimation(sprite, poseRankFor(stance));
    if (!animName) {
        throw new Error('character renderer found no static animation');
    }
    await sprite.prepareAnimation(animName, RENDER_SCALE, true);
    sprite.renderFrame(0);
    return saveToPng(canvas);
}

export async function renderCharacterSnapshot(lookString, boneName = null, stance = null) {
    if (!lookString) return null;
    const cacheKey = `${boneName || ''}|${stance || ''}|${lookString}`;
    if (imageCache.has(cacheKey)) return imageCache.get(cacheKey);
    if (pendingRequests.has(cacheKey)) return pendingRequests.get(cacheKey);

    const request = renderLookStringToBlob(lookString, boneName, stance)
        .then(async (blob) => {
            const [url, dataUrl] = await Promise.all([
                Promise.resolve(URL.createObjectURL(blob)),
                blobToPersistedDataUrl(blob),
            ]);
            const snapshot = { key: lookString, url, dataUrl };
            rememberImageSnapshot(cacheKey, snapshot);
            return snapshot;
        })
        .finally(() => {
            pendingRequests.delete(cacheKey);
        });

    pendingRequests.set(cacheKey, request);
    return request;
}

export async function renderCharacterImage(lookString, boneName = null, stance = null) {
    const snapshot = await renderCharacterSnapshot(lookString, boneName, stance);
    return snapshot?.url || null;
}

function prefersReducedMotion() {
    return typeof window !== 'undefined'
        && typeof window.matchMedia === 'function'
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** Drive one canvas on the idle loop.
 *
 *  The frames were always there: every pose we ship is a 48-frame `.dat` that
 *  the browser already downloads in full, so animating costs no extra bytes —
 *  `renderCharacterSnapshot` above just builds one frame of it and stops.
 *
 *  One animator per mounted canvas. The sprite is reused across look changes so
 *  equipping a hat re-fetches only the skin that moved and the loop keeps its
 *  place instead of snapping back to frame 0. Calls are serialized because
 *  `replaceLook` mutates the sprite it is reading.
 *
 *  This does not feed `onRenderCaptured`: the persisted card render still comes
 *  from `renderCharacterSnapshot`, so its bytes stay identical to what R2
 *  already holds. Capturing off the live canvas instead would hand a different
 *  frame to a content-addressed key on every save.
 */
export async function createCharacterAnimator(canvas) {
    const { Look, SpritePlayback, oppositeDirection } = await loadRenderer();
    const playback = new SpritePlayback();
    let baseAnimName = null;
    let facings = [];
    let direction = RENDER_DIRECTION;
    let bone = null;
    let stance = null;
    let disposed = false;
    let queue = Promise.resolve();

    // The pose goes in as its base name plus a separate `direction`, never as
    // the fully resolved `..._1`. That is the only form a facing can be
    // resolved from, including mirroring one that ships only the other way
    // round; handed a full name the renderer matches nothing and falls back to
    // the generic pose, which on a class bone does not exist.
    //
    // `boneName` rides along so `replaceLook` keeps the class skeleton, and so
    // its own frame-continuity check sees the bone it is already on rather than
    // deciding the look moved and restarting the loop.
    const playOptions = () => ({
        animName: baseAnimName,
        direction,
        boneName: bone || undefined,
        scale: RENDER_SCALE,
        audio: PLAYBACK_AUDIO,
        paused: prefersReducedMotion(),
    });

    /** Keep the facing to one this pose actually offers. */
    function clampDirection() {
        if (!facings.length || facings.includes(direction)) return;
        direction = facings.includes(RENDER_DIRECTION) ? RENDER_DIRECTION : facings[0];
    }

    /** Run a render, surviving a facing whose file is not actually there.
     *
     *  `availableDirections` reports what the bone *declares*, and the asset
     *  export prunes poses while `bone.json` goes on listing them — so a facing
     *  can be offered and still 404. That is what an environment whose assets
     *  lag behind looks like, and it used to take the whole character with it:
     *  the facings are published after this resolves, so failing here also cost
     *  the turn controls, and left the still wearing the previous render's
     *  scale and offset.
     *
     *  Dropping the facing is the cheap loss. Its mirror goes too — the renderer
     *  reaches one by flipping the other, so if the file is missing neither is
     *  reachable.
     */
    async function withFacingFallback(run) {
        try {
            await run();
        } catch (error) {
            if (direction === RENDER_DIRECTION) throw error;
            const mirrored = oppositeDirection(direction);
            facings = facings.filter((d) => d !== direction && d !== mirrored);
            direction = RENDER_DIRECTION;
            console.warn('[character-render] Facing unavailable, using the default:', error);
            await run();
        }
    }

    /** Resolve the pose on this sprite and the facings it offers. */
    function choosePose(sprite, nextStance) {
        const picked = pickStaticAnimation(sprite, poseRankFor(nextStance));
        if (!picked) throw new Error('character renderer found no static animation');
        baseAnimName = picked.slice(0, picked.lastIndexOf('_'));
        // Mirrored facings included, so a stance shipping two ways round still
        // turns four. The class idles ship five and reach all eight; the class
        // combat stances ship two and reach four — so this has to be re-read
        // whenever the pose changes, not only when the skeleton does.
        facings = sprite.availableDirections()[baseAnimName] || [RENDER_DIRECTION];
        clampDirection();
    }

    async function draw(lookString, boneName, nextStance, nextFacing) {
        if (disposed) return;
        // The caller owns the facing — a restored build brings one with it — so
        // take it before the pose is resolved and let that clamp it, rather than
        // rendering one way round and turning afterwards.
        if (Number.isInteger(nextFacing)) direction = nextFacing;
        // A different class is a different skeleton, and the pose names are
        // keyed to it, so both have to be re-resolved rather than reused. A
        // different stance is the same skeleton, so only the pose is.
        // The effective skeleton, not the class one: switching to or from the
        // neutral idle changes the bone as well as the pose, and that has to
        // rebuild rather than re-pose whatever is already loaded.
        const nextBone = boneForStance(boneName, nextStance);
        const boneChanged = nextBone !== bone;
        const stanceChanged = nextStance !== stance;
        bone = nextBone;
        stance = nextStance;
        if (playback.sprite && !boneChanged) {
            if (stanceChanged) choosePose(playback.sprite, nextStance);
            else clampDirection();
            await withFacingFallback(() => playback.replaceLook(Look.fromString(lookString), playOptions()));
        } else {
            if (playback.sprite) playback.stop();
            const sprite = await createSprite(lookString, canvas, nextBone);
            if (disposed) return;
            choosePose(sprite, nextStance);
            await withFacingFallback(() => playback.play(sprite, playOptions()));
        }
        if (disposed) playback.stop();
    }

    return {
        /** Show `lookString` on `boneName`, starting or retargeting the loop. */
        show(lookString, boneName = null, nextStance = null, nextFacing = null) {
            const run = () => draw(lookString, boneName, nextStance, nextFacing);
            queue = queue.then(run, run);
            return queue;
        },
        /** Where the character's own root sits in the canvas, in canvas px.
         *
         *  The renderer tracks it because the drawing is laid out around it, and
         *  `x` is flip-aware. The root is neither the middle of the canvas nor
         *  its bottom edge: props and effects grow the box to one side, and a
         *  stance can reach well below the feet, so the box is only a stand-in
         *  for the character when the pose happens to be tidy. `y` is measured
         *  down from the top, so the room below the root is height - y. */
        anchorX: () => playback.offset()?.x ?? null,
        anchorY: () => playback.offset()?.y ?? null,
        /** Facings the current pose can be turned to, mirrored ones included. */
        facings: () => facings,
        facing: () => direction,
        /** Turn to `next`, keeping the look, the sprite and the loop's place. */
        face(next) {
            queue = queue.then(async () => {
                const sprite = playback.sprite;
                if (disposed || !sprite || !facings.includes(next)) return;
                direction = next;
                await withFacingFallback(() => playback.play(sprite, playOptions()));
            }, () => {});
            return queue;
        },
        pause() {
            playback.pause();
        },
        resume() {
            // Reduced motion parks the loop on frame 0; resuming would undo the
            // very thing the setting asked for.
            if (!prefersReducedMotion()) playback.resume();
        },
        dispose() {
            disposed = true;
            playback.stop();
        },
    };
}

const bareRenderHeights = new Map();

async function measureBareReference(bareLookString) {
    const canvas = document.createElement('canvas');
    const sprite = await createSprite(bareLookString, canvas, null, { numberFrame: 1 });
    const animName = pickStaticAnimation(sprite, SIZE_REFERENCE_ANIM_RANK);
    if (!animName) throw new Error('character renderer found no static animation');
    await sprite.prepareAnimation(animName, RENDER_SCALE, true);
    return {
        height: canvas.height,
        belowRoot: canvas.height - (sprite.openGl?.offset?.y ?? canvas.height),
    };
}

/** This character measured bare, in the reference pose: the height it fills and
 *  how far its bounds reach below its own root.
 *
 *  This is a *scale* reference, not a description of what is on screen: divide
 *  the render's height by it and the result is a constant number of displayed
 *  pixels per canvas pixel, which is what holds the character still while the
 *  box around it changes. A tall hat then grows the box upward instead of
 *  shrinking the character to keep the total constant, and the same holds for a
 *  pose that reaches higher or crouches lower.
 *
 *  It is therefore measured on the generic skeleton in the plain idle —
 *  `SIZE_REFERENCE_ANIM_RANK` — and never on the pose being drawn. Measuring the
 *  drawn pose makes its own bounds cancel out, so the character is scaled by
 *  whatever the stance does: a bent Enutrof measures short and renders too big,
 *  a Huppermage's raised blade measures tall and renders too small.
 *
 *  `belowRoot` is the same idea one axis over. Anchoring the render's bottom
 *  edge only stands the character on the ground while its bounds stop at its
 *  feet, and a stance need not: a Sacrieur's reaches 67px further down than its
 *  own idle does, which floats it that far above the platform. Measured against
 *  this, the difference is a correction rather than a per-class fudge.
 *
 *  Cached per look because it is pure geometry, and measured rather than
 *  tabulated because bare heights run 390-514px across classes, so a single
 *  constant would resize the classes against each other.
 */
export function getBareRenderReference(bareLookString) {
    if (!bareLookString) return Promise.resolve(null);
    let pending = bareRenderHeights.get(bareLookString);
    if (!pending) {
        pending = measureBareReference(bareLookString).catch((error) => {
            bareRenderHeights.delete(bareLookString);
            throw error;
        });
        bareRenderHeights.set(bareLookString, pending);
    }
    return pending;
}
